import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCases, createCase, fetchCorrespondence, addReply } from './supportApi';
import {
    faPlus,
    faLifeRing,
    faChevronDown,
    faChevronRight,
    faTimes,
    faExclamationTriangle,
    faFire,
    faInfoCircle,
    faQuestionCircle,
    faCheckCircle,
    faTicketAlt,
    faClock,
    faEnvelope,
    faTag,
    faFileAlt,
    faUser,
    faServer,
    faCommentDots,
    faPaperPlane,
    faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';

// ─── Types ─────────────────────────────────────────────────────────────────

type Priority = 'business_critical' | 'p0_high' | 'p1_medium' | 'general';
type FormStep = 'select_priority' | 'fill_details' | 'submitted';

interface PriorityOption {
    id: Priority;
    label: string;
    badge: string;
    badgeColor: string;
    borderColor: string;
    bgColor: string;
    iconColor: string;
    responseTime: string;
    description: string;
    useCase: string;
}

interface CaseCorrespondence {
    id: string;
    sender: string;
    senderRole: 'customer' | 'support';
    timestamp: string;
    body: string;
}

interface SupportCase {
    id: string;
    subject: string;
    priority: Priority;
    status: 'Open' | 'In Progress' | 'Pending Customer' | 'Resolved' | 'Closed';
    caseType: string;
    category: string;
    created: string;
    lastUpdated: string;
    openedBy: string;
    language: string;
    affectedService: string;
    description: string;
    correspondence: CaseCorrespondence[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PRIORITY_OPTIONS: PriorityOption[] = [
    {
        id: 'business_critical',
        label: 'Business Critical',
        badge: 'CRITICAL',
        badgeColor: 'bg-red-100 text-red-700 border border-red-200',
        borderColor: 'border-red-300',
        bgColor: 'bg-red-50',
        iconColor: 'text-red-600',
        responseTime: '< 15 minutes',
        description: 'Your business is critically impacted. Production system is down or major functionality is unavailable.',
        useCase: 'Complete service outage, data loss, security breach affecting production.',
    },
    {
        id: 'p0_high',
        label: 'P0 – High Impact',
        badge: 'P0',
        badgeColor: 'bg-orange-100 text-orange-700 border border-orange-200',
        borderColor: 'border-orange-300',
        bgColor: 'bg-orange-50',
        iconColor: 'text-orange-500',
        responseTime: '< 1 hour',
        description: 'Significant business impact. A major feature or service is degraded but a workaround may exist.',
        useCase: 'Partial outage, severe performance degradation, critical feature unavailable.',
    },
    {
        id: 'p1_medium',
        label: 'P1 – Medium Impact',
        badge: 'P1',
        badgeColor: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
        borderColor: 'border-yellow-300',
        bgColor: 'bg-yellow-50',
        iconColor: 'text-yellow-600',
        responseTime: '< 4 hours',
        description: 'Some business impact. A non-critical feature is impaired or you need technical guidance.',
        useCase: 'Non-critical feature issue, configuration questions, best-practice guidance.',
    },
    {
        id: 'general',
        label: 'General Question',
        badge: 'INFO',
        badgeColor: 'bg-blue-100 text-blue-700 border border-blue-200',
        borderColor: 'border-blue-200',
        bgColor: 'bg-blue-50',
        iconColor: 'text-blue-500',
        responseTime: '< 24 hours',
        description: 'A general question, how-to inquiry, or feature request with no immediate business impact.',
        useCase: 'Documentation questions, feature requests, general how-to guidance.',
    },
];
const PRIORITY_TO_TYPE: Record<Priority, string> = {
    business_critical: 'business',
    p0_high: 'high impact',
    p1_medium: 'medium impact',
    general: 'general',
};

const TYPE_TO_PRIORITY: Record<string, Priority> = {
    'business': 'business_critical',
    'high impact': 'p0_high',
    'medium impact': 'p1_medium',
    'general': 'general',
};

const STATUS_MAP: Record<string, SupportCase['status']> = {
    pending_support: 'In Progress',
    pending_customer: 'Pending Customer',
    resolved: 'Resolved',
};


const PRIORITY_ICONS: Record<Priority, any> = {
    business_critical: faFire,
    p0_high: faExclamationTriangle,
    p1_medium: faInfoCircle,
    general: faQuestionCircle,
};

const STATUS_COLORS: Record<SupportCase['status'], string> = {
    'Open': 'bg-blue-100 text-blue-700',
    'In Progress': 'bg-indigo-100 text-indigo-700',
    'Pending Customer': 'bg-yellow-100 text-yellow-700',
    'Resolved': 'bg-green-100 text-green-700',
    'Closed': 'bg-gray-100 text-gray-600',
};

// const MOCK_CASES: SupportCase[] = [
//     {
//         id: 'CASE-20240001',
//         subject: 'EKS cluster autoscaler not working after upgrade',
//         priority: 'p0_high',
//         status: 'In Progress',
//         caseType: 'Technical',
//         category: 'EKS, Autoscaling',
//         created: '2026-02-24T10:22:00Z',
//         lastUpdated: '2026-02-25T14:05:00Z',
//         openedBy: 'anil@devopsx.ai',
//         language: 'English',
//         affectedService: 'Amazon EKS',
//         description:
//             'After upgrading our EKS cluster from 1.28 to 1.29, the Cluster Autoscaler stopped scaling out node groups. CPU requests are high and pods are stuck in Pending state. The autoscaler logs show: "No expansion possible".',
//         correspondence: [
//             {
//                 id: 'c1',
//                 sender: 'DevOpsX',
//                 senderRole: 'customer',
//                 timestamp: '2026-02-24T10:22:00Z',
//                 body: 'After upgrading EKS from 1.28 to 1.29, the Cluster Autoscaler stopped scaling out. Pods are stuck in Pending. CAS logs show "No expansion possible". Please help.',
//             },
//             {
//                 id: 'c2',
//                 sender: 'Support Engineer – Riya',
//                 senderRole: 'support',
//                 timestamp: '2026-02-24T11:45:00Z',
//                 body: "Hi, thank you for contacting us. Could you share the Cluster Autoscaler version and the output of `kubectl describe nodes` for one of the node groups? This will help us identify if it's a version compatibility issue.",
//             },
//             {
//                 id: 'c3',
//                 sender: 'DevOpsX',
//                 senderRole: 'customer',
//                 timestamp: '2026-02-24T13:10:00Z',
//                 body: 'CAS version is 1.28.0. Attached the describe-nodes output. One node shows taint "node.kubernetes.io/not-ready".',
//             },
//             {
//                 id: 'c4',
//                 sender: 'Support Engineer – Riya',
//                 senderRole: 'support',
//                 timestamp: '2026-02-25T14:05:00Z',
//                 body: 'Thank you for the details. The issue is a known compatibility gap — CAS 1.28.x is not fully compatible with EKS 1.29. Please upgrade CAS to 1.29.x via Helm: `--set image.tag=v1.29.0`. This should resolve the scaling issue. Let us know if the issue persists.',
//             },
//         ],
//     },
//     {
//         id: 'CASE-20240002',
//         subject: 'How to configure S3 lifecycle policies for cost optimization?',
//         priority: 'general',
//         status: 'Resolved',
//         caseType: 'How-To',
//         category: 'S3, Cost Optimization',
//         created: '2026-02-20T08:00:00Z',
//         lastUpdated: '2026-02-21T11:30:00Z',
//         openedBy: 'anil@devopsx.ai',
//         language: 'English',
//         affectedService: 'Amazon S3',
//         description:
//             'We have large S3 buckets with data that is rarely accessed after 90 days. We want to automatically transition objects to cheaper storage tiers.',
//         correspondence: [
//             {
//                 id: 'c1',
//                 sender: 'DevOpsX',
//                 senderRole: 'customer',
//                 timestamp: '2026-02-20T08:00:00Z',
//                 body: 'We want to move S3 objects to Glacier after 90 days and delete after 365 days. How do we configure lifecycle rules correctly?',
//             },
//             {
//                 id: 'c2',
//                 sender: 'Support Engineer – Alex',
//                 senderRole: 'support',
//                 timestamp: '2026-02-21T11:30:00Z',
//                 body: 'Hi! You can configure lifecycle rules in the S3 console under Bucket > Management > Lifecycle rules. For your use case: (1) Transition to S3-IA after 30 days, (2) Transition to Glacier Instant Retrieval after 90 days, (3) Expire objects after 365 days. You can also apply this via the AWS CLI using `aws s3api put-bucket-lifecycle-configuration`.',
//             },
//         ],
//     },
//     {
//         id: 'CASE-20240003',
//         subject: 'RDS Aurora connection pool exhaustion in production',
//         priority: 'business_critical',
//         status: 'Resolved',
//         caseType: 'Technical',
//         category: 'RDS Aurora, Database',
//         created: '2026-02-15T03:12:00Z',
//         lastUpdated: '2026-02-15T05:55:00Z',
//         openedBy: 'ops@devopsx.ai',
//         language: 'English',
//         affectedService: 'Amazon Aurora (PostgreSQL)',
//         description:
//             'Production Aurora PostgreSQL cluster is returning "too many connections" errors. Application is unable to connect. Impact: complete production outage for customer-facing APIs.',
//         correspondence: [
//             {
//                 id: 'c1',
//                 sender: 'DevOpsX',
//                 senderRole: 'customer',
//                 timestamp: '2026-02-15T03:12:00Z',
//                 body: 'CRITICAL: Aurora is rejecting all connections with "too many connections". All production APIs are down. Need immediate help.',
//             },
//             {
//                 id: 'c2',
//                 sender: 'Support Engineer – Sam',
//                 senderRole: 'support',
//                 timestamp: '2026-02-15T03:28:00Z',
//                 body: "We are escalating this immediately. To unblock now: run `SELECT pg_terminate_backend(pid) FROM pg_stat_activity WHERE state = 'idle' AND query_start < now() - interval '5 minutes';` to free idle connections.",
//             },
//             {
//                 id: 'c3',
//                 sender: 'DevOpsX',
//                 senderRole: 'customer',
//                 timestamp: '2026-02-15T03:40:00Z',
//                 body: 'The terminate query freed up connections. APIs are back. Total connections were at 2000 (max_connections=2000). How do we prevent this?',
//             },
//             {
//                 id: 'c4',
//                 sender: 'Support Engineer – Sam',
//                 senderRole: 'support',
//                 timestamp: '2026-02-15T05:55:00Z',
//                 body: 'Glad you are back online! For a permanent fix, deploy RDS Proxy in front of Aurora — it pools and reuses connections, supporting up to 8x more. Also consider setting `connection_throttling=1` on Aurora.',
//             },
//         ],
//     },
//     {
//         id: 'CASE-20240004',
//         subject: 'Lambda cold start latency exceeding SLA thresholds',
//         priority: 'p1_medium',
//         status: 'Pending Customer',
//         caseType: 'Technical',
//         category: 'Lambda, Performance',
//         created: '2026-02-22T15:30:00Z',
//         lastUpdated: '2026-02-23T10:00:00Z',
//         openedBy: 'dev@devopsx.ai',
//         language: 'English',
//         affectedService: 'AWS Lambda',
//         description:
//             'Lambda functions (Node.js 20, 512 MB) are experiencing cold start times of 800ms–1200ms on the p99. SLA requires < 500ms.',
//         correspondence: [
//             {
//                 id: 'c1',
//                 sender: 'DevOpsX',
//                 senderRole: 'customer',
//                 timestamp: '2026-02-22T15:30:00Z',
//                 body: 'Lambda cold starts are killing our p99 latency. Node.js 20, 512 MB, VPC attached. Cold starts at ~1100ms. SLA is 500ms. What can we do?',
//             },
//             {
//                 id: 'c2',
//                 sender: 'Support Engineer – Priya',
//                 senderRole: 'support',
//                 timestamp: '2026-02-23T10:00:00Z',
//                 body: 'VPC attachment is the biggest contributor to cold starts. We recommend: (1) Enable Provisioned Concurrency for peak periods, (2) Use Lambda SnapStart if migrating to Java 21 is an option, (3) Keep functions warm via CloudWatch scheduled events every 5 minutes. Could you share your average invocation rate?',
//             },
//         ],
//     },
// ];


// ─── Case Detail View ─────────────────────────────────────────────────────────

const CaseDetail: React.FC<{ caseItem: SupportCase; onBack: () => void; currentUser: any }> = ({ caseItem, onBack, currentUser }) => {
    const priorityOpt = PRIORITY_OPTIONS.find(o => o.id === caseItem.priority)!;
    const [replyValue, setReplyValue] = useState('');
    const [messages, setMessages] = useState<CaseCorrespondence[]>([]);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        fetchCorrespondence(caseItem.id).then(res => {
            if (res.success) {
                setMessages(res.data.map((m: any) => ({
                    id: m._id,
                    sender: m.user_name,
                    senderRole: (currentUser && m.user_id === currentUser.uid) ? 'customer' : 'support',
                    timestamp: m.timestamp,
                    body: m.text,
                })));
            }
        });
    }, [caseItem.id]);

    const handleSendReply = async () => {
        if (!replyValue.trim()) return;
        setSending(true);
        const res = await addReply(caseItem.id, replyValue, currentUser?.full_name || currentUser?.email || 'User');
        setSending(false);
        if (res.success) {
            setMessages(prev => [...prev, {
                id: res.data._id,
                sender: currentUser?.full_name || currentUser?.email || 'User',
                senderRole: 'customer',
                timestamp: new Date().toISOString(),
                body: replyValue,
            }]);
            setReplyValue('');
        }
    };

    return (
        <div className="space-y-2">
            {/* Back */}
            <button
                onClick={onBack}
                className="flex items-center space-x-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Back to Your Cases</span>
            </button>

            {/* Case Details Card */}
            <div className="bg-white border border-gray-300 rounded overflow-hidden">
                <div className="px-3 py-2 border-b border-gray-300 bg-gray-100">
                    <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faTicketAlt} className="text-blue-600 text-xs" />
                        <span className="text-sm font-bold text-gray-900">Case Details</span>
                    </div>
                </div>
                <div className="px-3 py-2 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                    <div>
                        <div className="text-xs font-medium text-gray-500 mb-0.5">Subject</div>
                        <div className="text-sm text-gray-900">{caseItem.subject}</div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 mb-0.5">Status</div>
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[caseItem.status]}`}>
                            {caseItem.status}
                        </span>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 mb-0.5">Case ID</div>
                        <div className="text-sm font-mono text-gray-800">{caseItem.id}</div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 mb-0.5">Severity</div>
                        <div className="flex items-center space-x-1.5">
                            <FontAwesomeIcon icon={PRIORITY_ICONS[caseItem.priority]} className={`${priorityOpt.iconColor} text-xs`} />
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityOpt.badgeColor}`}>
                                {priorityOpt.badge}
                            </span>
                            <span className="text-xs text-gray-600">{priorityOpt.label}</span>
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 mb-0.5">Created</div>
                        <div className="text-sm text-gray-800">{new Date(caseItem.created).toLocaleString()}</div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 mb-0.5">Affected Service</div>
                        <div className="text-sm text-gray-800">{caseItem.affectedService}</div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 mb-0.5">Opened By</div>
                        <div className="text-sm text-gray-800">{caseItem.openedBy}</div>
                    </div>
                </div>
            </div>

            {/* Correspondence */}
            <div className="bg-white border border-gray-300 rounded overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-300 bg-gray-100">
                    <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faCommentDots} className="text-gray-600 text-xs" />
                        <span className="text-sm font-bold text-gray-900">Correspondence</span>
                    </div>
                </div>
                <div className="divide-y divide-gray-100">
                    {messages.map(msg => (
                        <div key={msg.id} className="px-3 py-2.5">
                            <div className="flex items-start space-x-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.senderRole === 'support' ? 'bg-orange-100' : 'bg-blue-100'}`}>
                                    <FontAwesomeIcon
                                        icon={faUser}
                                        className={`text-xs ${msg.senderRole === 'support' ? 'text-orange-600' : 'text-blue-600'}`}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-semibold text-gray-800">{msg.sender}</span>
                                        <span className="text-xs text-gray-400">{new Date(msg.timestamp).toLocaleString()}</span>
                                    </div>
                                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{msg.body}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Reply box */}
                {caseItem.status !== 'Resolved' && caseItem.status !== 'Closed' && (
                    <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
                        <textarea
                            rows={3}
                            value={replyValue}
                            onChange={e => setReplyValue(e.target.value)}
                            placeholder="Type your reply here…"
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-gray-400 resize-none mb-2"
                        />
                        <div className="flex justify-end">
                            <button
                                onClick={handleSendReply}
                                disabled={!replyValue.trim() || sending}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-medium rounded-lg transition-colors"
                            >
                                <FontAwesomeIcon icon={faPaperPlane} />
                                <span>{sending ? 'Sending...' : 'Send Reply'}</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Your Cases Sub-tab Content ───────────────────────────────────────────────

const YourCases: React.FC<{ currentUser: any; onCasesLoaded: (count: number) => void }> = ({ currentUser, onCasesLoaded }) => {

    const [selectedCase, setSelectedCase] = useState<SupportCase | null>(null);
    const [cases, setCases] = useState<SupportCase[]>([]);
    const [loading, setLoading] = useState(true);

    const mapBackendCase = (c: any): SupportCase => ({
        id: c._id,
        subject: c.subject,
        priority: TYPE_TO_PRIORITY[c.type] || 'general',
        status: STATUS_MAP[c.status] || 'Open',
        caseType: 'Technical',
        category: c.affected_service,
        created: c.created_at,
        lastUpdated: c.updated_at,
        openedBy: c.contact_email,
        language: 'English',
        affectedService: c.affected_service,
        description: '',
        correspondence: [],
    });

    useEffect(() => {
        fetchCases().then(res => {
            if (res.success) {
                const mapped = res.data.map(mapBackendCase);
                setCases(mapped);
                onCasesLoaded(mapped.length);
            }
            setLoading(false);
        });
    }, []);

    if (selectedCase) {
        return <CaseDetail caseItem={selectedCase} onBack={() => setSelectedCase(null)} currentUser={currentUser} />;
    }

    if (loading) {
        return <div className="py-10 text-center text-sm text-gray-500">Loading cases...</div>;
    }

    return (
        <div className="space-y-2">
            {/* Summary stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                    { label: 'Total Cases', value: cases.length, color: 'text-gray-900', bg: 'bg-white', border: 'border-gray-300' },
                    { label: 'In Progress', value: cases.filter(c => c.status === 'In Progress').length, color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
                    { label: 'Pending', value: cases.filter(c => c.status === 'Pending Customer').length, color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200' },
                    { label: 'Resolved', value: cases.filter(c => c.status === 'Resolved').length, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
                ].map(stat => (
                    <div key={stat.label} className={`px-2 py-2 rounded border ${stat.border} ${stat.bg}`}>
                        <div className={`text-xl font-bold ${stat.color}`}>{stat.value}</div>
                        <div className="text-xs text-gray-600 mt-0.5 font-medium">{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* Cases list */}
            <div className="bg-white border border-gray-300 rounded overflow-hidden">
                <div className="flex items-center px-3 py-2 border-b border-gray-300 bg-gray-100">
                    <FontAwesomeIcon icon={faTicketAlt} className="text-gray-600 mr-2 text-xs" />
                    <span className="text-sm font-bold text-gray-900">All Cases</span>
                    <span className="ml-2 text-xs text-gray-500">({cases.length})</span>
                </div>

                <div className="divide-y divide-gray-100">
                    {cases.map(c => {
                        const opt = PRIORITY_OPTIONS.find(o => o.id === c.priority)!;
                        return (
                            <button
                                key={c.id}
                                onClick={() => setSelectedCase(c)}
                                className="w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors group"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center flex-wrap gap-1.5 mb-1">
                                            <span className="text-xs font-mono text-gray-400">{c.id}</span>
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${opt.badgeColor}`}>
                                                {opt.badge}
                                            </span>
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status]}`}>
                                                {c.status}
                                            </span>
                                        </div>
                                        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-700 transition-colors">
                                            {c.subject}
                                        </p>
                                        <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                                            <span className="flex items-center space-x-1">
                                                <FontAwesomeIcon icon={faServer} className="text-gray-300" />
                                                <span>{c.affectedService}</span>
                                            </span>
                                            <span className="flex items-center space-x-1">
                                                <FontAwesomeIcon icon={faClock} className="text-gray-300" />
                                                <span>Updated {new Date(c.lastUpdated).toLocaleDateString()}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <FontAwesomeIcon
                                        icon={faChevronRight}
                                        className="text-gray-300 group-hover:text-blue-500 text-xs ml-4 mt-2 flex-shrink-0 transition-colors"
                                    />
                                </div>
                            </button>
                        );
                    })}
                </div>

                {cases.length === 0 && (
                    <div className="py-10 text-center">
                        <FontAwesomeIcon icon={faLifeRing} className="text-3xl text-gray-300 mb-3" />
                        <p className="text-sm text-gray-500 font-medium">No support cases yet</p>
                        <p className="text-xs text-gray-400 mt-1">Click "Create Case" to open a new support request</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const SupportTab: React.FC = () => {
    // Use main app's auth token — no separate support login needed
    const currentUser = (() => {
        try {
            const u = localStorage.getItem('auth_user');
            return u ? JSON.parse(u) : null;
        } catch { return null; }
    })();

    const [supportSubTab, setSupportSubTab] = useState<'create_case' | 'your_cases'>('create_case');
    const [casesCount, setCasesCount] = useState(0);
    const [showCreateCase, setShowCreateCase] = useState(false);
    const [step, setStep] = useState<FormStep>('select_priority');
    const [selectedPriority, setSelectedPriority] = useState<Priority | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [form, setForm] = useState({
        subject: '',
        description: '',
        affectedService: '',
        contactEmail: '',
    });

    const selectedOption = PRIORITY_OPTIONS.find(o => o.id === selectedPriority);


    const handleOpenCreate = () => {
        setShowCreateCase(true);
        setStep('select_priority');
        setSelectedPriority(null);
        setIsDropdownOpen(false);
        setForm({ subject: '', description: '', affectedService: '', contactEmail: '' });
    };

    const handleClose = () => {
        setShowCreateCase(false);
        setStep('select_priority');
        setSelectedPriority(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const res = await createCase({
            type: PRIORITY_TO_TYPE[selectedPriority!],
            subject: form.subject,
            description: form.description,
            affected_service: form.affectedService || 'N/A',
            contact_email: form.contactEmail || currentUser?.email || '',
            user_name: currentUser?.full_name || currentUser?.email || 'User',
        });
        if (res.success) {
            setStep('submitted');
        } else if (res.quota_exhausted) {
            alert('Premium ticket quota exhausted! You can still raise general or medium impact cases.');
        } else {
            alert(res.message || 'Failed to create case');
        }
    };
    return (
        <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">

            {/* ── Sub-tab Navigation ── */}
            <div className="flex space-x-1 border-b border-gray-300">
                {(['create_case', 'your_cases'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setSupportSubTab(tab)}
                        className={`pb-2.5 px-4 border-b-2 font-medium text-sm transition-colors flex items-center space-x-1.5 ${supportSubTab === tab
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <FontAwesomeIcon
                            icon={tab === 'create_case' ? faPlus : faTicketAlt}
                            className="text-xs"
                        />
                        <span>{tab === 'create_case' ? 'Create Case' : 'Your Cases'}</span>
                        {tab === 'your_cases' && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${supportSubTab === tab ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                {casesCount}
                                {/* {MOCK_CASES.length} */}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* ── Your Cases sub-tab ── */}
            {supportSubTab === 'your_cases' && <YourCases currentUser={currentUser} onCasesLoaded={setCasesCount} />}
            {/* ── Create Case sub-tab content ── */}
            {supportSubTab === 'create_case' && <>

                {/* ── Header — Support Center info + Create Case button ── */}
                <div className="bg-white border border-gray-300 rounded p-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <div className="w-7 h-7 bg-blue-100 rounded flex items-center justify-center">
                                <FontAwesomeIcon icon={faLifeRing} className="text-blue-600 text-base" />
                            </div>
                            <div>
                                <div className="text-base font-bold text-gray-900">Support Center</div>
                                <div className="text-xs text-gray-600">Manage and track your support cases</div>
                            </div>
                        </div>
                        <button
                            onClick={handleOpenCreate}
                            className="flex items-center space-x-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded"
                        >
                            <FontAwesomeIcon icon={faPlus} className="text-xs" />
                            <span>Create Case</span>
                        </button>
                    </div>
                </div>

                {/* ── Create Case Panel — opens below the header ── */}
                {showCreateCase && (
                    <div className="bg-white border border-gray-300 rounded overflow-hidden">

                        {step === 'submitted' ? (
                            <div className="p-6 text-center">
                                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-2xl" />
                                </div>
                                <h4 className="text-base font-semibold text-gray-900 mb-1">Case Submitted Successfully</h4>
                                <p className="text-sm text-gray-600 mb-1">Your case has been created and assigned a case ID.</p>
                                <p className="text-xs text-gray-500 mb-5">
                                    Expected response time:{' '}
                                    <span className="font-medium text-blue-600">{selectedOption?.responseTime}</span>
                                </p>
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                                >
                                    Done
                                </button>
                            </div>
                        ) : (
                            <div className="p-3 space-y-3">
                                {/* Priority Dropdown */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-2">
                                        Select Priority / Case Type
                                        <span className="text-red-500 ml-1">*</span>
                                    </label>

                                    <button
                                        type="button"
                                        onClick={() => setIsDropdownOpen(prev => !prev)}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-lg text-sm transition-colors ${selectedPriority
                                            ? `${selectedOption!.borderColor} ${selectedOption!.bgColor}`
                                            : 'border-gray-300 bg-white hover:border-gray-400'
                                            }`}
                                    >
                                        {selectedPriority && selectedOption ? (
                                            <div className="flex items-center space-x-2">
                                                <FontAwesomeIcon icon={PRIORITY_ICONS[selectedPriority]} className={`${selectedOption.iconColor} text-sm`} />
                                                <span className="font-medium text-gray-900">{selectedOption.label}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selectedOption.badgeColor}`}>
                                                    {selectedOption.badge}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-sm">Choose a priority level…</span>
                                        )}
                                        <FontAwesomeIcon
                                            icon={isDropdownOpen ? faChevronDown : faChevronRight}
                                            className="text-gray-400 text-xs"
                                        />
                                    </button>

                                    {isDropdownOpen && (
                                        <div className="mt-1 border border-gray-200 rounded-lg shadow-lg bg-white overflow-hidden z-10 relative">
                                            {PRIORITY_OPTIONS.map((option, idx) => (
                                                <button
                                                    key={option.id}
                                                    type="button"
                                                    onClick={() => { setSelectedPriority(option.id); setIsDropdownOpen(false); }}
                                                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${idx < PRIORITY_OPTIONS.length - 1 ? 'border-b border-gray-100' : ''
                                                        } ${selectedPriority === option.id ? 'bg-blue-50' : ''}`}
                                                >
                                                    <div className="flex items-center justify-between mb-0.5">
                                                        <div className="flex items-center space-x-2">
                                                            <FontAwesomeIcon icon={PRIORITY_ICONS[option.id]} className={`${option.iconColor} text-sm`} />
                                                            <span className="text-sm font-medium text-gray-900">{option.label}</span>
                                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${option.badgeColor}`}>
                                                                {option.badge}
                                                            </span>
                                                        </div>
                                                        <span className="text-xs text-gray-500 flex items-center space-x-1">
                                                            <FontAwesomeIcon icon={faClock} className="text-gray-400" />
                                                            <span>{option.responseTime}</span>
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1 ml-5">{option.description}</p>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {selectedPriority && selectedOption && !isDropdownOpen && (
                                        <div className={`mt-2 flex items-center space-x-2 px-2.5 py-1.5 rounded-lg border ${selectedOption.borderColor} ${selectedOption.bgColor}`}>
                                            <FontAwesomeIcon icon={PRIORITY_ICONS[selectedPriority]} className={`${selectedOption.iconColor} text-xs`} />
                                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${selectedOption.badgeColor}`}>{selectedOption.badge}</span>
                                            <span className="text-xs text-gray-700 flex-1 truncate">{selectedOption.description}</span>
                                            <span className="text-xs text-gray-500 whitespace-nowrap"><FontAwesomeIcon icon={faClock} className="mr-1 text-gray-400" />{selectedOption.responseTime}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Case Details Form */}
                                {selectedPriority && (
                                    <form onSubmit={handleSubmit} className="space-y-3" noValidate>
                                        <div className="border-t border-gray-100 pt-3">
                                            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                                                Case Details
                                            </h4>

                                            <div className="mb-2">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    <FontAwesomeIcon icon={faTag} className="mr-1 text-gray-400" />
                                                    Subject <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={form.subject}
                                                    onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                                                    placeholder="Brief description of the issue"
                                                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-gray-400"
                                                />
                                            </div>

                                            <div className="mb-2">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    <FontAwesomeIcon icon={faFileAlt} className="mr-1 text-gray-400" />
                                                    Description <span className="text-red-500">*</span>
                                                </label>
                                                <textarea
                                                    required
                                                    rows={3}
                                                    value={form.description}
                                                    onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                                    placeholder="Describe the issue in detail. Include steps to reproduce, error messages, and expected vs actual behavior."
                                                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-gray-400 resize-none"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Affected Service / Resource
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={form.affectedService}
                                                        onChange={e => setForm(p => ({ ...p, affectedService: e.target.value }))}
                                                        placeholder="e.g. EKS, S3, RDS…"
                                                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-gray-400"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        <FontAwesomeIcon icon={faEnvelope} className="mr-1 text-gray-400" />
                                                        Contact Email
                                                    </label>
                                                    <input
                                                        type="email"
                                                        value={form.contactEmail}
                                                        onChange={e => setForm(p => ({ ...p, contactEmail: e.target.value }))}
                                                        placeholder="you@company.com"
                                                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-gray-400"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
                                            <button
                                                type="button"
                                                onClick={handleClose}
                                                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={!form.subject.trim() || !form.description.trim()}
                                                className="px-4 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors"
                                            >
                                                Submit Case
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Priority Reference — compact single-row pill strip ── */}
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-1">SLA:</span>
                    {PRIORITY_OPTIONS.map(option => (
                        <div key={option.id} className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-full border ${option.borderColor} ${option.bgColor}`}>
                            <FontAwesomeIcon icon={PRIORITY_ICONS[option.id]} className={`${option.iconColor} text-xs`} />
                            <span className={`text-xs font-semibold`}>{option.badge}</span>
                            <span className="text-xs text-gray-500">{option.responseTime}</span>
                        </div>
                    ))}
                </div>

            </>}

        </div >
    );
};

export default SupportTab;
